import React from 'react';
import { Stamp, MapPin } from 'lucide-react';
import type { TravelStamp } from '../../data/go2LegendeMock';

interface TravelPassportProps {
  stamps: TravelStamp[];
}

const regionNames: Record<string, string> = {
  balkans: 'Balkan',
  western_europe: 'Zapadna Evropa',
  central_europe: 'Centralna Evropa',
  mediterranean: 'Mediteran',
  scandinavia: 'Skandinavija',
  eastern_europe: 'Istočna Evropa',
};

const regionColors: Record<string, string> = {
  balkans: 'bg-blue-100 text-blue-700',
  western_europe: 'bg-purple-100 text-purple-700',
  central_europe: 'bg-green-100 text-green-700',
  mediterranean: 'bg-orange-100 text-orange-700',
  scandinavia: 'bg-cyan-100 text-cyan-700',
  eastern_europe: 'bg-pink-100 text-pink-700',
};

export const TravelPassport: React.FC<TravelPassportProps> = ({ stamps }) => {
  const totalSlots = 8;
  const emptySlots = Math.max(0, totalSlots - stamps.length);

  // Get unique regions visited
  const visitedRegions = [...new Set(stamps.map(s => s.region))];

  // Generate random rotation for stamp cards
  const getRandomRotation = (index: number): number => {
    const rotations = [-2, -1, 0, 1, 2, -1.5, 1.5];
    return rotations[index % rotations.length];
  };

  return (
    <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl shadow-sm p-6 border border-amber-100">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-2xl">📕</span>
            <h3 className="text-xl font-bold text-gray-900">Putni Pasoš</h3>
          </div>
          <p className="text-sm text-gray-600">
            <span className="font-semibold text-go2-pink">{stamps.length}</span> / 20 pečata sakupljeno
          </p>
        </div>
        <Stamp className="w-8 h-8 text-amber-600" />
      </div>

      {/* Stamps Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        {/* Filled stamps */}
        {stamps.map((stamp, index) => (
          <div
            key={stamp.id}
            className="bg-white rounded-lg p-4 border-2 border-amber-300 shadow-md hover:shadow-lg transition-all transform hover:scale-105"
            style={{
              transform: `rotate(${getRandomRotation(index)}deg)`,
            }}
          >
            <div className="text-center">
              <div className="text-4xl mb-2">{stamp.emoji}</div>
              <h4 className="font-bold text-gray-900 text-sm mb-0.5">{stamp.destination}</h4>
              <p className="text-xs text-gray-500 mb-1">{stamp.country}</p>
              <p className="text-xs text-gray-400 mb-2">
                {new Date(stamp.date).toLocaleDateString('sr-RS', {
                  month: 'short',
                  year: 'numeric',
                })}
              </p>
              <p className="text-xs italic text-gray-400 line-clamp-1">{stamp.tripName}</p>
            </div>
          </div>
        ))}

        {/* Empty slots */}
        {[...Array(emptySlots)].map((_, index) => (
          <div
            key={`empty-${index}`}
            className="bg-white rounded-lg p-4 border-2 border-dashed border-gray-300 shadow-sm flex flex-col items-center justify-center text-center min-h-[150px] hover:border-amber-300 transition-colors"
          >
            <div className="text-4xl mb-2 opacity-30">❓</div>
            <p className="text-xs text-gray-400 font-medium">Sledeća destinacija?</p>
          </div>
        ))}
      </div>

      {/* Region Badges */}
      {visitedRegions.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <MapPin className="w-4 h-4 text-gray-600" />
            <h4 className="text-sm font-semibold text-gray-700">Posećeni regioni</h4>
          </div>
          <div className="flex flex-wrap gap-2">
            {visitedRegions.map((region) => (
              <span
                key={region}
                className={`px-3 py-1 rounded-full text-xs font-medium ${
                  regionColors[region] || 'bg-gray-100 text-gray-700'
                }`}
              >
                {regionNames[region] || region}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Progress hint */}
      {stamps.length > 0 && stamps.length < 20 && (
        <div className="mt-4 pt-4 border-t border-amber-200">
          <p className="text-xs text-center text-gray-500">
            Još <span className="font-bold text-go2-pink">{20 - stamps.length}</span> pečata do pune kolekcije! 🎯
          </p>
        </div>
      )}

      {stamps.length === 0 && (
        <div className="text-center py-8">
          <div className="text-5xl mb-3">🗺️</div>
          <p className="text-gray-500 text-sm font-medium">Počni svoje putovanje i sakupljaj pečate!</p>
          <p className="text-xs text-gray-400 mt-1">Svaka destinacija donosi novi pečat u tvoj pasoš</p>
        </div>
      )}
    </div>
  );
};
